import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { SmsFindingSeverity } from '#/shared/api/generated/communication/models'
import type {
  SmsBlockerRead,
  SmsFindingRead,
  SmsPreviewVerdict,
} from '#/shared/api/generated/communication/models'
import { previewSmsCostApiV1SmsMessagesPreviewPost } from '#/shared/api/generated/communication/theCargoCommunication'
import { useDebouncedValue } from '#/shared/lib/hooks'

/** The composer keystroke settles before the preview round trip fires. */
const PREVIEW_DEBOUNCE_MS = 400

/**
 * What the server says about a draft SMS before it is sent, split into the two
 * things a composer does with it:
 *
 * - `findings` — content signals that get a message carrier-spam-blocked (fancy
 *   font, spam-trigger words, URL shorteners, ALL CAPS, missing opt-out). These
 *   are advisory: `verdict` grades them, but the message still sends. Reducing
 *   them lowers the odds of a `40002`; it is never a guarantee.
 * - `blockers` — hard reasons *we* refuse the send (sender not SMS-capable, no
 *   consent, route in spam cooldown, quota spent). These are certain, so the
 *   composer disables Send and shows them rather than letting the user fire a
 *   message that cannot leave.
 */
export interface SmsDeliverability {
  verdict: SmsPreviewVerdict | null
  findings: SmsFindingRead[]
  blockers: SmsBlockerRead[]
  /** Our own gates refuse this send — disable Send and show `blockers`. */
  blocked: boolean
}

export const EMPTY_SMS_DELIVERABILITY: SmsDeliverability = {
  verdict: null,
  findings: [],
  blockers: [],
  blocked: false,
}

/** Worst first, so the row that decides whether the message sends leads. */
const SEVERITY_RANK: Record<SmsFindingSeverity, number> = {
  [SmsFindingSeverity.block]: 0,
  [SmsFindingSeverity.warn]: 1,
  [SmsFindingSeverity.info]: 2,
}

function bySeverity(a: SmsFindingRead, b: SmsFindingRead): number {
  return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
}

/** The deliverability slice shared by the single and the mass preview responses. */
interface PreviewDeliverabilityFields {
  verdict?: SmsPreviewVerdict | null
  findings?: SmsFindingRead[]
  can_send?: boolean
  blockers?: SmsBlockerRead[]
}

/**
 * `can_send` is the server's verdict on its own gates, and `blockers` carries
 * the reasons. We only treat the send as blocked when a reason came with it —
 * a disabled Send button with nothing to explain it is worse than letting the
 * request go out and surfacing the API error.
 */
export function toSmsDeliverability(
  preview: PreviewDeliverabilityFields,
): SmsDeliverability {
  const blockers = preview.blockers ?? []
  return {
    verdict: preview.verdict ?? null,
    findings: [...(preview.findings ?? [])].sort(bySeverity),
    blockers,
    blocked: preview.can_send === false && blockers.length > 0,
  }
}

interface SmsPreviewInput {
  /** The plain-text body as it would go on the wire (editor HTML already flattened). */
  body: string
  /** Sending line — drives sender eligibility and the per-segment cost tier. */
  userPhoneId: string | null | undefined
  /** Recipient. Only with it does the server check consent, route cooldown and
   *  undeliverable contacts; without it only sender/quota blockers come back. */
  toNumber?: string
  /** Promotional traffic is held to the stricter opt-out check. */
  isMarketing?: boolean
  /** Set false where the composer is closed, so a hidden draft costs nothing. */
  enabled?: boolean
}

/**
 * Server-side dry run of a single SMS, on debounce, for the compose views. The
 * body is linted and the sender/recipient pair checked against the gates the
 * real send path runs — without queueing anything.
 *
 * Note the response's `body` (the sanitized text) is deliberately unused: every
 * send path already runs `sanitizeSmsText` locally, which keeps the counter and
 * the optimistic bubble equal to the wire body without waiting for a round trip.
 * This hook is here for the deliverability read the client cannot compute.
 */
export function useSmsPreview({
  body,
  userPhoneId,
  toNumber,
  isMarketing = false,
  enabled = true,
}: SmsPreviewInput): SmsDeliverability {
  const debouncedBody = useDebouncedValue(body, PREVIEW_DEBOUNCE_MS)
  const { data } = useQuery({
    queryKey: [
      'sms-preview',
      userPhoneId ?? '',
      toNumber ?? '',
      isMarketing,
      debouncedBody,
    ],
    queryFn: async ({ signal }) => {
      const response = await previewSmsCostApiV1SmsMessagesPreviewPost(
        {
          message: debouncedBody,
          user_phone_id: userPhoneId ?? null,
          to_number: toNumber ?? null,
          is_marketing: isMarketing,
        },
        signal,
      )
      return response.data
    },
    select: toSmsDeliverability,
    // Findings for the previous keystroke stay on screen while the next round
    // trip is in flight, so the notice doesn't flicker on every character.
    placeholderData: keepPreviousData,
    enabled: enabled && debouncedBody.length > 0 && Boolean(userPhoneId),
  })
  return data ?? EMPTY_SMS_DELIVERABILITY
}
